/**
 * 交期排程表單 ↔ API payload。
 * 列鍵：PO／schedule line 的 `mtr.id` → write `mtrId`（snake `mtr_id`）。
 * **不**處理／**不**送 `purchaseOrderLineNo`；**禁止**以陣列 index 當識別鍵。
 */

/** 建議計劃量：未排程量 → openQty；皆無則空字串。 */
export function resolveSuggestedPlannedQty(line) {
  const unscheduled = Number(line?.unscheduledQty);
  if (!Number.isNaN(unscheduled) && unscheduled > 0) {
    return unscheduled;
  }
  const open = Number(line?.openQty);
  if (!Number.isNaN(open) && open > 0) {
    return open;
  }
  return '';
}

/** PO detail line → 排程表單列；缺 mtr.id 略過。 */
export function mapPurchaseOrderLineToArrivalScheduleFormLine(line) {
  const mtrId = line?.mtr?.id ?? '';
  if (!mtrId) {
    return null;
  }

  return {
    mtrId,
    mtrNo: line?.mtr?.mtrNo ?? '',
    mtrName: line?.mtr?.name ?? '',
    unscheduledQty: line?.unscheduledQty ?? line?.openQty ?? '',
    plannedQty: resolveSuggestedPlannedQty(line),
  };
}

/** 排程 detail line → 表單列。 */
export function mapArrivalScheduleLineToFormLine(line) {
  const mtrId = line?.mtr?.id ?? line?.mtrId ?? '';
  if (!mtrId) {
    return null;
  }

  return {
    mtrId,
    mtrNo: line?.mtr?.mtrNo ?? '',
    mtrName: line?.mtr?.name ?? '',
    unscheduledQty: line?.unscheduledQty ?? '',
    plannedQty: line?.plannedQty ?? '',
  };
}

/** Create：由 PO detail 種子。 */
export function buildArrivalScheduleCreateDefaultValues(poDetail) {
  const lines = (poDetail?.lines ?? [])
    .map(mapPurchaseOrderLineToArrivalScheduleFormLine)
    .filter(Boolean);

  return {
    expectedArrivalDate: '',
    remark: '',
    lines,
  };
}

/**
 * Edit：優先 schedule.lines（依 mtr.id）；若無則回退 PO lines 並套 schedule 頂層 plannedQty（單列時）。
 */
export function buildArrivalScheduleEditDefaultValues(schedule, poDetail) {
  let lines;

  if (Array.isArray(schedule?.lines) && schedule.lines.length > 0) {
    lines = schedule.lines
      .map(mapArrivalScheduleLineToFormLine)
      .filter(Boolean);
  } else {
    const poLines = (poDetail?.lines ?? [])
      .map(mapPurchaseOrderLineToArrivalScheduleFormLine)
      .filter(Boolean);
    if (poLines.length === 1 && schedule?.plannedQty != null && schedule.plannedQty !== '') {
      lines = [{ ...poLines[0], plannedQty: schedule.plannedQty }];
    } else {
      lines = poLines;
    }
  }

  return {
    expectedArrivalDate: schedule?.expectedArrivalDate ?? '',
    remark: schedule?.remark ?? '',
    lines,
  };
}

/**
 * 表單 → create／update body（camel；API 層 keysToSnake）。
 * 僅送 mtrId＋plannedQty；不送 purchaseOrderLineNo／index。
 */
export function buildArrivalScheduleWritePayload(values, { updatedAt } = {}) {
  const lines = (values?.lines ?? [])
    .map((line) => {
      const mtrId = line?.mtrId?.trim();
      if (!mtrId) {
        return null;
      }
      return {
        mtrId,
        plannedQty: Number(line.plannedQty),
      };
    })
    .filter(Boolean);

  const payload = {
    expectedArrivalDate: values?.expectedArrivalDate ?? '',
    remark: values?.remark?.trim() ? values.remark.trim() : null,
    lines,
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

/**
 * PO 狀態 → 建立後排程狀態提示（後端定稿）。
 * DRAFT／SUBMITTED → DRAFT；APPROVED／PARTIALLY_RECEIVED → CONFIRMED。
 */
export function resolveArrivalScheduleStatusOnCreate(poStatus) {
  if (poStatus === 'DRAFT' || poStatus === 'SUBMITTED') {
    return 'DRAFT';
  }
  if (poStatus === 'APPROVED' || poStatus === 'PARTIALLY_RECEIVED') {
    return 'CONFIRMED';
  }
  return null;
}
